import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useTranslation } from "react-i18next";
import { useUpdateProject } from "../../services/projectService";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import type { Project } from "../../services/projectService";

// Zod validation schema for progress update
const progressUpdateSchema = z
  .object({
    raised_amount: z.number().min(0, "Raised amount must be positive"),
  })
  .refine((data) => data.raised_amount >= 0, {
    message: "Raised amount cannot be negative",
  });

type ProgressUpdateFormData = z.infer<typeof progressUpdateSchema>;

interface ProjectProgressModalProps {
  project: Project | null;
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

const ProjectProgressModal: React.FC<ProjectProgressModalProps> = ({
  project,
  isOpen,
  onClose,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const updateProjectMutation = useUpdateProject();

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
  } = useForm<ProgressUpdateFormData>({
    resolver: zodResolver(progressUpdateSchema),
    defaultValues: {
      raised_amount: project?.raised_amount || 0,
    },
  });

  // Watch raised_amount to calculate progress
  const watchedRaisedAmount = watch("raised_amount");
  const goalAmount = project?.goal_amount || 0;
  const progressPercentage =
    goalAmount > 0
      ? Math.min((watchedRaisedAmount / goalAmount) * 100, 100)
      : 0;

  // Update form when project changes
  useEffect(() => {
    if (project) {
      reset({
        raised_amount: project.raised_amount,
      });
    }
  }, [project, reset]);

  const onSubmit = async (data: ProgressUpdateFormData) => {
    if (!project) return;

    // Validate that raised amount doesn't exceed goal amount
    if (data.raised_amount > goalAmount) {
      // This should be caught by form validation, but double-check
      return;
    }

    try {
      await updateProjectMutation.mutateAsync({
        id: project.id,
        projectData: { raised_amount: data.raised_amount },
      });
      reset();
      onClose();
      onSuccess?.();
    } catch (error) {
      console.error("Failed to update project progress:", error);
    }
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  if (!project) return null;

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>
            {t("admin.projects.updateProgress", "Update Progress")}
          </DialogTitle>
          <DialogDescription>
            {t(
              "admin.projects.updateProgressDescription",
              "Update the raised amount for this project. The progress percentage will be calculated automatically."
            )}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          {/* Current Progress Display */}
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-sm font-medium">
                  {t("admin.projects.table.goalAmount", "Goal Amount")}
                </Label>
                <div className="text-lg font-semibold">
                  {project.currency} {goalAmount.toLocaleString()}
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium">
                  {t("admin.projects.table.raisedAmount", "Current Raised")}
                </Label>
                <div className="text-lg font-semibold">
                  {project.currency} {project.raised_amount.toLocaleString()}
                </div>
              </div>
            </div>

            {/* Progress Bar */}
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span>{t("admin.projects.table.progress", "Progress")}</span>
                <span>{progressPercentage.toFixed(1)}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2.5">
                <div
                  className="bg-blue-600 h-2.5 rounded-full transition-all duration-300"
                  style={{ width: `${progressPercentage}%` }}
                ></div>
              </div>
            </div>
          </div>

          {/* Raised Amount Input */}
          <div className="space-y-2">
            <Label htmlFor="raised_amount">
              {t("admin.projects.table.raisedAmount", "Raised Amount")}
            </Label>
            <Input
              {...register("raised_amount", {
                valueAsNumber: true,
                validate: (value) => {
                  if (value > goalAmount) {
                    return t(
                      "admin.projects.raisedAmountExceedsGoal",
                      "Raised amount cannot exceed goal amount"
                    );
                  }
                  return true;
                },
              })}
              type="number"
              step="0.01"
              min="0"
              max={goalAmount}
              placeholder="0.00"
            />
            {errors.raised_amount && (
              <p className="text-sm text-red-600">
                {errors.raised_amount.message}
              </p>
            )}
            <p className="text-xs text-gray-500">
              {t("admin.projects.maxAmount", "Maximum: {{amount}}", {
                amount: `${project.currency} ${goalAmount.toLocaleString()}`,
              })}
            </p>
          </div>

          {/* New Progress Preview */}
          {watchedRaisedAmount !== project.raised_amount && (
            <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
              <h4 className="text-sm font-medium text-blue-900 mb-2">
                {t("admin.projects.preview", "Preview")}
              </h4>
              <div className="space-y-1 text-sm text-blue-800">
                <div>
                  {t("admin.projects.newRaisedAmount", "New Raised Amount")}:{" "}
                  {project.currency} {watchedRaisedAmount.toLocaleString()}
                </div>
                <div>
                  {t("admin.projects.newProgress", "New Progress")}:{" "}
                  {progressPercentage.toFixed(1)}%
                </div>
                <div>
                  {t("admin.projects.remaining", "Remaining")}:{" "}
                  {project.currency}{" "}
                  {(goalAmount - watchedRaisedAmount).toLocaleString()}
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={handleClose}>
              {t("admin.projects.cancel", "Cancel")}
            </Button>
            <Button
              type="submit"
              disabled={
                updateProjectMutation.isPending ||
                watchedRaisedAmount > goalAmount
              }
            >
              {updateProjectMutation.isPending
                ? t("admin.projects.updating", "Updating...")
                : t("admin.projects.update", "Update Progress")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ProjectProgressModal;
